import * as React from 'react';
import styled from 'styled-components';
import {
  CarouselProvider,
  Slider,
  Slide,
  ButtonBack,
  ButtonNext,
} from 'pure-react-carousel';
import { WebP } from '../../Defaults/WebP';
import { MetaTags } from '../../Defaults/MetaTags';
import { Helmet } from 'react-helmet';
import { getImagePathNew, prop } from '../../../utilities';
import { margin, rem } from 'polished';
// import 'pure-react-carousel/dist/react-carousel.es.css';

interface Props {
  items: any;
  user: any;
}

const DesktopBannerWrapper = styled.div`
  position: relative;

  ${({ theme }) => theme.mediab.m810`
    display: none
  `}
`;

const MobileBannerWrapper = styled.div`
  display: none;

  ${({ theme }) => theme.mediab.m810`
    display: block;
    min-height: ${rem(280)};
  `}
`;

const BannerWrapper = styled.div`
  margin-top: 32px;
  margin-bottom: 32px;

  ${({ theme }) => theme.mediab.l1300`
  margin-bottom: 12px;
  `}
`;

const ButtonBackWrapper = styled.div`
  position: absolute;
  top: calc(50% - 25px);
  z-index: 1;
  color: transparent;

  button {
    background-image: url('/images/caret-left.svg');
    background-position: center center;
    background-repeat: no-repeat;
    background-color: transparent;
    box-shadow: none;
    outline: 0;
    display: block;
    border: none;
    width: 50px;
    height: 50px;
    color: transparent;
  }

  @media only screen and (max-width: 860px) {
    display: none;
  }
`;

const ButtonNextWrapper = styled.div`
  position: absolute;
  right: 0;
  top: calc(50% + 5px);
  z-index: 1;

  button {
    background-image: url('/images/caret-left.svg');
    background-position: center center;
    background-repeat: no-repeat;
    background-color: transparent;
    transform: translateY(-50%) rotate(180deg);
    color: transparent;
    border: none;
    box-shadow: none;
    outline: 0;
    display: block;
    width: 50px;
    height: 50px;
  }

  @media only screen and (max-width: 860px) {
    display: none;
  }
`;

// Compare function for sorting banners
const compare = (a: any, b: any) => {
  if (a.b_sort < b.b_sort) {
    return -1;
  }
  if (a.b_sort > b.b_sort) {
    return 1;
  }
  return 0;
};

// Get specific banners for user and sort them with respect to specific sort parameter
const getBannersForCurrentUser = (desktopBanners, mobileBanners, user) => {
  const userDesktopBanners: any = [];
  const userMobileBanners: any = [];
  const isb2b = user ? user.b2b : false;
  const u = isb2b ? 'B2b' : 'B2c';

  desktopBanners.forEach(banner => {
    if (banner.users === u || banner.users === 'all') {
      userDesktopBanners.push(banner);
    }
  });

  mobileBanners.forEach(banner => {
    if (banner.users === u || banner.users === 'all') {
      userMobileBanners.push(banner);
    }
  });

  userDesktopBanners.sort(compare);
  userMobileBanners.sort(compare);

  return { userDesktopBanners, userMobileBanners };
};

function ZemplinSlider(props: Props) {
  const { user } = props;
  const desktopBanners = props.items.filter(i => i.device === 'desktop');
  const mobileBanners = props.items.filter(i => i.device === 'phone');
  // const tabletBanners = props.items.filter(i => i.device === 'tablet');

  const { userDesktopBanners, userMobileBanners } = getBannersForCurrentUser(
    desktopBanners,
    mobileBanners,
    user,
  );

  return (
    <>
      <Helmet>
        <link
          rel="preload"
          as={'image'}
          href={getImagePathNew(
            prop(userMobileBanners, '0.b_path'),
            { width: 700, height: 600 },
            false,
            false,
            true,
          )}
        />
      </Helmet>
      <BannerWrapper>
        <DesktopBannerWrapper>
          <CarouselProvider
            naturalSlideWidth={1500}
            naturalSlideHeight={200}
            totalSlides={userDesktopBanners.length}
            isIntrinsicHeight={true}
            interval={5000}
            isPlaying={true}
            infinite={true}
          >
            {userDesktopBanners.length > 1 && (
              <ButtonBackWrapper>
                <ButtonBack>d</ButtonBack>
              </ButtonBackWrapper>
            )}
            <Slider>
              {userDesktopBanners.map((item, index) => {
                return (
                  <Slide key={index} index={index}>
                    <a href={item.b_url} title={item.b_description}>
                      <WebP
                        image={item.b_path}
                        alt={item.b_name}
                        width={1500}
                        height={200}
                      />
                    </a>
                  </Slide>
                );
              })}
            </Slider>
            {userDesktopBanners.length > 1 && (
              <ButtonNextWrapper>
                <ButtonNext>d</ButtonNext>
              </ButtonNextWrapper>
            )}
          </CarouselProvider>
        </DesktopBannerWrapper>
        <MobileBannerWrapper>
          <CarouselProvider
            naturalSlideWidth={450}
            naturalSlideHeight={450}
            totalSlides={userMobileBanners.length}
            isIntrinsicHeight={true}
            interval={5000}
            isPlaying={true}
            infinite={true}
          >
            {userMobileBanners.length > 1 && (
              <ButtonBackWrapper>
                <ButtonBack>d</ButtonBack>
              </ButtonBackWrapper>
            )}
            <Slider>
              {userMobileBanners.map((item, index) => {
                return (
                  <Slide key={index} index={index} style={{ display: 'flex' }}>
                    <StyledLink href={item.b_url} title={item.b_description}>
                      <StyledWebP
                        image={item.b_path}
                        alt={item.b_name}
                        width={700}
                        height={700}
                        cover={true}
                        lazy={index !== 0}
                      />
                    </StyledLink>
                  </Slide>
                );
              })}
            </Slider>
            {userMobileBanners.length > 1 && (
              <ButtonNextWrapper>
                <ButtonNext>d</ButtonNext>
              </ButtonNextWrapper>
            )}
          </CarouselProvider>
        </MobileBannerWrapper>
      </BannerWrapper>{' '}
    </>
  );
}

const StyledLink = styled.a``;

const StyledWebP = styled(WebP)`
  max-width: ${rem(450)};
  margin: 0 auto;
  display: block;
`;

export { ZemplinSlider };
